import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Search, Plus, Trash2, RotateCcw, Eye, EyeOff, Star, StarOff,
  Crown, Copy, Radio, CheckCircle2, XCircle, Loader2,
  ChevronLeft, ChevronRight, ArrowUpDown,
  Tv, X, Save, Upload, AlertTriangle, Clock, Wifi,
  WifiOff, Settings, Activity, Play, Square, Download, CalendarClock, Zap,
} from "lucide-react";
import { ImageSourceSelector } from "@/components/ImageSourceSelector";
import ImportChannels from "./ImportChannels";

const STREAM_TYPES = [
  { value: "hls", label: "HLS (.m3u8)" },
  { value: "dash", label: "DASH (.mpd)" },
  { value: "mpegts", label: "MPEG-TS" },
  { value: "flv", label: "FLV" },
  { value: "webrtc", label: "WebRTC" },
  { value: "embed", label: "Embed/iFrame" },
  { value: "other", label: "Other" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Channels" },
  { value: "draft", label: "Draft (imported)" },
  { value: "published", label: "Published" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "featured", label: "Featured" },
  { value: "premium", label: "Premium" },
  { value: "deleted", label: "Deleted" },
];

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className={cn("bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full my-8", wide ? "max-w-4xl" : "max-w-2xl")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 sticky top-0 bg-slate-900 rounded-t-2xl z-10">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400" data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function StreamTestResult({ result }) {
  if (!result) return null;
  const colors = {
    reachable: "text-green-400 bg-green-500/10 border-green-500/30",
    timeout: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    unreachable: "text-red-400 bg-red-500/10 border-red-500/30",
    auth_failure: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    not_found: "text-red-400 bg-red-500/10 border-red-500/30",
    blocked: "text-slate-400 bg-slate-500/10 border-slate-500/30",
    error: "text-red-400 bg-red-500/10 border-red-500/30",
    no_url: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  };
  const icons = { reachable: CheckCircle2, timeout: Clock, unreachable: WifiOff, auth_failure: AlertTriangle, error: XCircle };
  const Icon = icons[result.result] || XCircle;
  return (
    <div className={cn("flex items-start gap-3 p-3 rounded-xl border text-sm", colors[result.result] || colors.error)} data-testid="stream-test-result">
      <Icon size={16} className="mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium capitalize">{result.result?.replace(/_/g, " ")}</p>
        <p className="text-xs opacity-80 mt-0.5">{result.message}</p>
      </div>
    </div>
  );
}

const HEALTH_META = {
  online: { label: "Online", dot: "bg-green-500", text: "text-green-400", emoji: "🟢" },
  slow: { label: "Slow", dot: "bg-yellow-500", text: "text-yellow-400", emoji: "🟡" },
  offline: { label: "Offline", dot: "bg-red-500", text: "text-red-400", emoji: "🔴" },
  timeout: { label: "Timeout", dot: "bg-orange-500", text: "text-orange-400", emoji: "⚠" },
  invalid_url: { label: "Invalid URL", dot: "bg-red-600", text: "text-red-400", emoji: "❌" },
  auth_required: { label: "Auth Required", dot: "bg-amber-500", text: "text-amber-400", emoji: "🔐" },
  unsupported_format: { label: "Unsupported", dot: "bg-purple-500", text: "text-purple-400", emoji: "📡" },
  redirect_loop: { label: "Redirect Loop", dot: "bg-pink-500", text: "text-pink-400", emoji: "⚠" },
};
const HEALTH_FILTERS = [
  { value: "", label: "All Health" },
  { value: "online", label: "🟢 Online" },
  { value: "failed", label: "🔴 Offline" },
  { value: "slow", label: "🟡 Slow" },
  { value: "never", label: "Never Tested" },
];

function relTime(iso) {
  if (!iso) return "Never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function HealthCell({ ch, onTest, testing }) {
  const st = ch.health_status;
  const meta = HEALTH_META[st];
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={`health-cell-${ch.id}`}>
      <button onClick={() => onTest(ch.id)} disabled={testing}
        className="p-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white flex-shrink-0"
        title="Test Stream" data-testid={`test-stream-${ch.id}`}>
        {testing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
      </button>
      <div className="min-w-0">
        {meta ? (
          <>
            <div className={cn("flex items-center gap-1.5 text-[11px] font-semibold", meta.text)}>
              <span className={cn("w-1.5 h-1.5 rounded-full", meta.dot)} /> {meta.label}
            </div>
            <div className="text-[10px] text-slate-500 truncate">
              {ch.health_response_time != null ? `${ch.health_response_time}ms · ` : ""}{(ch.health_stream_type || "").toUpperCase() || "—"} · {relTime(ch.health_last_tested)}
            </div>
            {ch.health_backup_status && <div className="text-[10px] text-slate-500">Backup: {HEALTH_META[ch.health_backup_status]?.label || ch.health_backup_status}</div>}
          </>
        ) : (
          <span className="text-[11px] text-slate-600">Never tested</span>
        )}
      </div>
    </div>
  );
}

function HealthProgressModal({ job, onCancel, onClose }) {
  if (!job) return null;
  const pct = job.total ? Math.round((job.completed / job.total) * 100) : 0;
  const done = job.status !== "running";
  const eta = job.eta_seconds;
  const etaStr = eta == null ? "—" : eta > 60 ? `${Math.floor(eta / 60)}m ${eta % 60}s` : `${eta}s`;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="health-progress-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Activity size={18} className="text-blue-400" /> {done ? "Scan Complete" : "Scanning Channels..."}</h3>
          {done && <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="health-progress-close"><X size={20} /></button>}
        </div>
        <div className="w-full h-3 rounded-full bg-slate-800 overflow-hidden mb-2">
          <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} data-testid="health-progress-bar" />
        </div>
        <div className="text-sm text-slate-300 mb-4" data-testid="health-progress-count">{job.completed} / {job.total} completed ({pct}%)</div>
        {!done && job.current_channel && <p className="text-xs text-slate-500 mb-4">Running: <span className="text-white">{job.current_channel}</span></p>}
        <div className="grid grid-cols-3 gap-2 text-center mb-5">
          {[["Completed", job.completed], ["Remaining", job.remaining], ["Passed", job.passed],
            ["Failed", job.failed], ["Timeout", job.timeout], ["ETA", done ? "—" : etaStr]].map(([l, v]) => (
            <div key={l} className="bg-slate-950 rounded-lg py-2">
              <div className="text-lg font-bold text-white">{v}</div>
              <div className="text-[10px] text-slate-500">{l}</div>
            </div>
          ))}
        </div>
        {job.status === "cancelled" && <p className="text-xs text-amber-400 mb-3">Cancelled — completed results were saved.</p>}
        {!done ? (
          <button onClick={onCancel} data-testid="health-stop-btn" className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30 font-medium"><Square size={15} /> Stop Testing</button>
        ) : (
          <button onClick={onClose} className="w-full px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-500">Done</button>
        )}
      </div>
    </div>
  );
}

function ChannelForm({ channel, categories, countries, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name: "", slug: "", description_en: "", description_fr: "",
    logo_url: "", desktop_cover: "", tablet_cover: "", mobile_cover: "",
    stream_url: "", backup_stream_url: "", embed_url: "", stream_type: "hls",
    category_id: "", country_id: "",
    is_featured: 0, featured_order: 0, is_premium: 0, is_free: 0,
    is_active: 0, is_live: 1, is_hd: 0, display_order: 0,
    website_url: "", social_links: {},
    seo_title_en: "", seo_title_fr: "", seo_description_en: "", seo_description_fr: "",
    ...channel,
  });

  const [tab, setTab] = useState("general");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [imageField, setImageField] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const testStream = async (url) => {
    if (!channel?.id) return;
    setTesting(true); setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/channels/${channel.id}/test-stream`,
        { url }, { withCredentials: true });
      setTestResult(res.data);
    } catch {
      setTestResult({ result: "error", message: "Failed to test stream" });
    }
    setTesting(false);
  };

  const generateSlug = () => {
    const slug = form.name.toLowerCase().replace(/[^\w\s-]/g, "").replace(/[\s_]+/g, "-").replace(/-+/g, "-").trim();
    set("slug", slug);
  };

  const TABS = [
    { key: "general", label: "General" },
    { key: "media", label: "Media" },
    { key: "stream", label: "Stream" },
    { key: "seo", label: "SEO" },
  ];

  return (
    <div>
      <div className="flex gap-1 mb-6 bg-slate-800/50 rounded-xl p-1" data-testid="channel-form-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={cn("flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all",
              tab === t.key ? "bg-blue-600 text-white shadow" : "text-slate-400 hover:text-white")}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "general" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Channel Name *</label>
              <input value={form.name} onChange={e => set("name", e.target.value)} data-testid="channel-name"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="Channel name" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Slug</label>
              <div className="flex gap-2">
                <input value={form.slug} onChange={e => set("slug", e.target.value)} data-testid="channel-slug"
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="channel-slug" />
                <button onClick={generateSlug} className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-xs text-slate-300 hover:text-white whitespace-nowrap">Auto</button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Category</label>
              <select value={form.category_id || ""} onChange={e => set("category_id", e.target.value || null)} data-testid="channel-category"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="">No category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name_en || c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Country</label>
              <select value={form.country_id || ""} onChange={e => set("country_id", e.target.value || null)} data-testid="channel-country"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value="">No country</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Description (EN)</label>
            <textarea value={form.description_en} onChange={e => set("description_en", e.target.value)} rows={3} data-testid="channel-desc-en"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none" placeholder="English description..." />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Description (FR)</label>
            <textarea value={form.description_fr} onChange={e => set("description_fr", e.target.value)} rows={3} data-testid="channel-desc-fr"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none" placeholder="Description en français..." />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Display Order</label>
              <input type="number" value={form.display_order} onChange={e => set("display_order", parseInt(e.target.value) || 0)} data-testid="channel-order"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Featured Order</label>
              <input type="number" value={form.featured_order} onChange={e => set("featured_order", parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Website URL</label>
              <input value={form.website_url} onChange={e => set("website_url", e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="https://..." />
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            {[
              { key: "is_active", label: "Active", on: "bg-green-500/20 text-green-400 border-green-500/30" },
              { key: "is_featured", label: "Featured", on: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
              { key: "is_premium", label: "Premium", on: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
              { key: "is_free", label: "Free", on: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
              { key: "is_live", label: "LIVE", on: "bg-red-500/20 text-red-400 border-red-500/30" },
              { key: "is_hd", label: "HD", on: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" },
            ].map(({ key, label, on }) => (
              <button key={key} onClick={() => set(key, form[key] ? 0 : 1)} data-testid={`toggle-${key}`}
                className={cn("flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all",
                  form[key] ? on : "bg-slate-800 text-slate-500 border-slate-700")}>
                {form[key] ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === "media" && (
        <div className="space-y-5">
          <div>
            <label className="text-xs text-slate-500 mb-2 block">Channel Logo</label>
            {form.logo_url ? (
              <div className="flex items-center gap-4">
                <img src={form.logo_url} alt="Logo" className="w-16 h-16 rounded-xl object-contain bg-slate-800 border border-slate-700" />
                <div className="flex gap-2">
                  <button onClick={() => setImageField("logo_url")} className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white">Change</button>
                  <button onClick={() => set("logo_url", "")} className="px-3 py-1.5 bg-red-900/30 border border-red-500/30 rounded-lg text-xs text-red-400">Remove</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setImageField("logo_url")} data-testid="upload-logo"
                className="w-full py-8 border-2 border-dashed border-slate-700 rounded-xl text-slate-500 hover:text-slate-300 hover:border-slate-600 transition-colors text-sm">
                <Upload size={20} className="mx-auto mb-2" /> Upload Logo
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { key: "desktop_cover", label: "Desktop Cover" },
              { key: "tablet_cover", label: "Tablet Cover" },
              { key: "mobile_cover", label: "Mobile Cover" },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="text-xs text-slate-500 mb-2 block">{label}</label>
                {form[key] ? (
                  <div className="relative group">
                    <img src={form[key]} alt={label} className="w-full aspect-video rounded-lg object-cover bg-slate-800 border border-slate-700" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <button onClick={() => setImageField(key)} className="p-2 bg-slate-800 rounded-lg text-white text-xs">Change</button>
                      <button onClick={() => set(key, "")} className="p-2 bg-red-800 rounded-lg text-white text-xs">Remove</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setImageField(key)}
                    className="w-full aspect-video border-2 border-dashed border-slate-700 rounded-lg text-slate-500 hover:text-slate-300 hover:border-slate-600 transition-colors text-xs flex flex-col items-center justify-center">
                    <Upload size={16} className="mb-1" /> {label}
                  </button>
                )}
              </div>
            ))}
          </div>
          {imageField && (
            <Modal title={`Select Image — ${imageField.replace(/_/g, " ")}`} onClose={() => setImageField(null)}>
              <ImageSourceSelector value={form[imageField]} onChange={(url) => { set(imageField, url); setImageField(null); }} label={imageField.replace(/_/g, " ")} />
            </Modal>
          )}
        </div>
      )}

      {tab === "stream" && (
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Stream Type</label>
            <select value={form.stream_type} onChange={e => set("stream_type", e.target.value)} data-testid="stream-type"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
              {STREAM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Primary Stream URL</label>
            <div className="flex gap-2">
              <input value={form.stream_url} onChange={e => set("stream_url", e.target.value)} data-testid="stream-url"
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white font-mono" placeholder="https://example.com/stream.m3u8" />
              {channel?.id && (
                <button onClick={() => testStream(form.stream_url)} disabled={testing || !form.stream_url} data-testid="test-stream-btn"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-500 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
                  {testing ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />} Test
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Backup Stream URL</label>
            <div className="flex gap-2">
              <input value={form.backup_stream_url} onChange={e => set("backup_stream_url", e.target.value)}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white font-mono" placeholder="https://backup.example.com/stream.m3u8" />
              {channel?.id && form.backup_stream_url && (
                <button onClick={() => testStream(form.backup_stream_url)} disabled={testing}
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg text-xs font-medium hover:bg-slate-600 disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
                  {testing ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />} Test
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Embed URL (iFrame)</label>
            <input value={form.embed_url} onChange={e => set("embed_url", e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white font-mono" placeholder="https://embed.example.com/player" />
          </div>
          {testResult && <StreamTestResult result={testResult} />}
        </div>
      )}

      {tab === "seo" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">SEO Title (EN)</label>
              <input value={form.seo_title_en} onChange={e => set("seo_title_en", e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="SEO title in English" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">SEO Title (FR)</label>
              <input value={form.seo_title_fr} onChange={e => set("seo_title_fr", e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white" placeholder="Titre SEO en français" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">SEO Description (EN)</label>
              <textarea value={form.seo_description_en} onChange={e => set("seo_description_en", e.target.value)} rows={3}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none" placeholder="SEO description..." />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">SEO Description (FR)</label>
              <textarea value={form.seo_description_fr} onChange={e => set("seo_description_fr", e.target.value)} rows={3}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white resize-none" placeholder="Description SEO..." />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-800">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 text-sm">Cancel</button>
        <button onClick={() => onSave(form)} disabled={saving || !form.name} data-testid="save-channel-btn"
          className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {channel?.id ? "Save Changes" : "Create Channel"}
        </button>
      </div>
    </div>
  );
}

export default function AdminChannels() {
  const [channels, setChannels] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selected, setSelected] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [excluded, setExcluded] = useState(new Set());
  const [editChannel, setEditChannel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [healthFilter, setHealthFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [languages, setLanguages] = useState([]);
  const [testingRow, setTestingRow] = useState(null);
  const [job, setJob] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [autoScan, setAutoScan] = useState("manual");
  const [showImport, setShowImport] = useState(false);

  const deleteAllDrafts = async () => {
    try {
      const s = await axios.get(`${API}/admin/channels/drafts/summary`, { withCredentials: true });
      if (!s.data.total) { alert("No draft channels to delete."); return; }
      if (!window.confirm(`Archive ALL ${s.data.total} draft channels?\n(${s.data.with_views} with views, ${s.data.with_backups} with backups). Published channels are NOT affected. This is a soft-delete (recoverable).`)) return;
      const r = await axios.post(`${API}/admin/channels/drafts/delete-all`, { permanent: false }, { withCredentials: true });
      alert(`Archived ${r.data.affected} draft channels.`);
      fetchChannels();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const fetchChannels = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page, limit: 25, search, status: statusFilter, sort: sortField, order: sortOrder });
      if (categoryFilter) params.set("category_id", categoryFilter);
      if (countryFilter) params.set("country_id", countryFilter);
      if (languageFilter) params.set("language", languageFilter);
      if (healthFilter) params.set("health", healthFilter);
      const res = await axios.get(`${API}/admin/channels?${params}`, { withCredentials: true });
      setChannels(res.data.channels || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load channels");
    }
    setLoading(false);
  }, [page, search, statusFilter, categoryFilter, countryFilter, languageFilter, healthFilter, sortField, sortOrder]);

  // Reset selection whenever the filter set changes
  useEffect(() => {
    setSelected(new Set()); setAllMatching(false); setExcluded(new Set());
  }, [search, statusFilter, categoryFilter, countryFilter, languageFilter, healthFilter]);

  const currentFilters = () => ({
    search, status: statusFilter, category_id: categoryFilter,
    country_id: countryFilter, language: languageFilter, health: healthFilter,
  });
  const effectiveCount = allMatching ? Math.max(0, total - excluded.size) : selected.size;
  const selectAllMatching = () => { setAllMatching(true); setExcluded(new Set()); setSelected(new Set()); };
  const clearSelection = () => { setAllMatching(false); setExcluded(new Set()); setSelected(new Set()); };
  const isRowSelected = (id) => allMatching ? !excluded.has(id) : selected.has(id);

  const fetchRefData = useCallback(async () => {
    try {
      const [catRes, countryRes, langRes] = await Promise.all([
        axios.get(`${API}/categories`, { withCredentials: true }),
        axios.get(`${API}/countries`, { withCredentials: true }),
        axios.get(`${API}/admin/languages?status=active`, { withCredentials: true }),
      ]);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setCountries(Array.isArray(countryRes.data) ? countryRes.data : []);
      setLanguages(langRes.data?.languages || []);
    } catch { /* use empty */ }
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);
  useEffect(() => { fetchRefData(); }, [fetchRefData]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editChannel?.id) {
        await axios.put(`${API}/admin/channels/${editChannel.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/channels`, form, { withCredentials: true });
      }
      setEditChannel(null); fetchChannels();
    } catch { /* handle error */ }
    setSaving(false);
  };

  const quickAction = async (id, action, data) => {
    try { await axios.patch(`${API}/admin/channels/${id}/${action}`, data, { withCredentials: true }); fetchChannels(); } catch { /* */ }
  };

  const duplicateChannel = async (id) => {
    await axios.post(`${API}/admin/channels/${id}/duplicate`, {}, { withCredentials: true }); fetchChannels();
  };

  const deleteChannel = async (ch) => {
    if (!confirm(`Delete "${ch.name}"?`)) return;
    await axios.delete(`${API}/admin/channels/${ch.id}`, { withCredentials: true }); fetchChannels();
  };

  const restoreChannel = async (id) => {
    await axios.post(`${API}/admin/channels/${id}/restore`, {}, { withCredentials: true }); fetchChannels();
  };

  const DESTRUCTIVE = ["delete", "deactivate", "make_free", "make_premium"];
  const bulkAction = async (action, value) => {
    const count = effectiveCount;
    if (count === 0) return;
    if (DESTRUCTIVE.includes(action) && !confirm(`Apply "${action.replace(/_/g, ' ')}" to ${count} channel(s)? This cannot be undone in bulk.`)) return;
    const body = allMatching
      ? { selection_mode: "all_matching", filters: currentFilters(), excluded_ids: [...excluded], action, value }
      : { ids: [...selected], action, value };
    const res = await axios.post(`${API}/admin/channels/bulk`, body, { withCredentials: true });
    clearSelection(); fetchChannels();
    return res.data;
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortOrder(o => o === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortOrder("asc"); }
  };

  const toggleSelect = (id) => {
    if (allMatching) {
      setExcluded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
  };
  const toggleSelectAll = () => {
    if (allMatching) { clearSelection(); return; }
    selected.size === channels.length ? setSelected(new Set()) : setSelected(new Set(channels.map(c => c.id)));
  };

  // ---- Channel Health ----
  const testRow = async (id) => {
    setTestingRow(id);
    try { await axios.post(`${API}/admin/channels/${id}/health-test`, {}, { withCredentials: true }); fetchChannels(); }
    catch { /* */ } finally { setTestingRow(null); }
  };

  const startBulk = async (scope) => {
    const body = { scope };
    if (scope === "selected") { if (selected.size === 0) return; body.ids = [...selected]; }
    try {
      const res = await axios.post(`${API}/admin/channels/health/bulk`, body, { withCredentials: true });
      setShowProgress(true);
      pollJob(res.data.job_id);
    } catch (e) { alert(e.response?.data?.detail || "Could not start scan"); }
  };

  const pollJob = useCallback((jobId) => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await axios.get(`${API}/admin/channels/health/job/${jobId}`, { withCredentials: true });
        setJob(r.data);
        if (r.data.status === "running") { setTimeout(tick, 1500); }
        else { fetchChannels(); }
      } catch { /* */ }
    };
    tick();
    return () => { stopped = true; };
  }, [fetchChannels]);

  const stopBulk = async () => {
    if (!job) return;
    try { await axios.post(`${API}/admin/channels/health/job/${job.id}/cancel`, {}, { withCredentials: true }); } catch { /* */ }
  };

  const retestFailed = async () => {
    try {
      const res = await axios.post(`${API}/admin/channels/health/bulk`, { scope: "filtered", filters: { health_status: "failed" } }, { withCredentials: true });
      setShowProgress(true); pollJob(res.data.job_id);
    } catch (e) { alert(e.response?.data?.detail || "No failed channels or scan running"); }
  };

  const deactivateFailed = async () => {
    if (!confirm("Deactivate ALL channels that failed their last health check? This will hide them from the site.")) return;
    const res = await axios.post(`${API}/admin/channels/health/deactivate-failed`, {}, { withCredentials: true });
    alert(`${res.data.affected} channel(s) deactivated`);
    fetchChannels();
  };

  const exportHealth = () => window.open(`${API}/admin/channels/health/export/csv`, "_blank");

  const [healthCfg, setHealthCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);
  const loadHealthCfg = () => axios.get(`${API}/admin/channels/health/schedule`, { withCredentials: true })
    .then((r) => { setHealthCfg(r.data); setAutoScan(r.data.interval || "daily"); }).catch(() => {});
  const saveHealthCfg = async () => {
    setCfgSaving(true);
    try {
      await axios.post(`${API}/admin/channels/health/schedule`, healthCfg, { withCredentials: true });
      await loadHealthCfg(); setShowCfg(false);
    } catch (e) { alert(e.response?.data?.detail || "Failed to save"); }
    finally { setCfgSaving(false); }
  };
  const setCfg = (k, v) => setHealthCfg(c => ({ ...c, [k]: v }));

  useEffect(() => { loadHealthCfg(); }, []);

  if (error && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4" data-testid="channels-error">
        <AlertTriangle size={32} className="text-red-400" />
        <h3 className="text-lg font-semibold text-white">Failed to load channels</h3>
        <p className="text-sm text-slate-400">{error}</p>
        <button onClick={fetchChannels} className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium">Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{total} channels</span>
          <div className="flex items-center gap-2">
            <button onClick={deleteAllDrafts} data-testid="delete-all-drafts-btn"
              className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/20 flex items-center gap-2">
              <Trash2 size={14} /> Delete Drafts
            </button>
            <button onClick={() => setShowImport(true)} data-testid="import-channels-btn"
              className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-medium hover:bg-slate-700 flex items-center gap-2">
              <Download size={14} /> Import
            </button>
            <button onClick={() => setEditChannel({})} data-testid="add-channel-btn"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 flex items-center gap-2">
              <Plus size={14} /> Add Channel
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-grow max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="channel-search"
              className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-white placeholder:text-slate-600" placeholder="Search channels..." />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} data-testid="status-filter"
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} data-testid="category-filter"
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
            <option value="">All Categories</option>
            <option value="none">— No Category —</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name_en || c.name}</option>)}
          </select>
          <select value={countryFilter} onChange={e => { setCountryFilter(e.target.value); setPage(1); }} data-testid="country-filter-select"
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
            <option value="">All Countries</option>
            <option value="none">— No Country —</option>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={languageFilter} onChange={e => { setLanguageFilter(e.target.value); setPage(1); }} data-testid="language-filter"
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
            <option value="">All Languages</option>
            <option value="none">— No Language —</option>
            {languages.map(l => <option key={l.code} value={l.code}>{l.name_en}</option>)}
          </select>
          <select value={healthFilter} onChange={e => { setHealthFilter(e.target.value); setPage(1); }} data-testid="health-filter"
            className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-300">
            {HEALTH_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        {/* Channel Health toolbar */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-800/30 border border-slate-700/40 rounded-xl" data-testid="health-toolbar">
          <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Activity size={14} className="text-blue-400" /> Channel Health</span>
          <button onClick={() => startBulk("selected")} disabled={selected.size === 0} data-testid="test-selected-btn"
            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:text-white disabled:opacity-40 flex items-center gap-1.5"><Play size={12} /> Test Selected{selected.size ? ` (${selected.size})` : ""}</button>
          <button onClick={() => startBulk("all")} data-testid="test-all-btn"
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-500 flex items-center gap-1.5"><Zap size={12} /> Test All</button>
          <button onClick={retestFailed} data-testid="retest-failed-btn"
            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:text-white flex items-center gap-1.5"><RotateCcw size={12} /> Retest Failed</button>
          <button onClick={deactivateFailed} data-testid="deactivate-failed-btn"
            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-red-400 hover:bg-red-900/20 flex items-center gap-1.5"><EyeOff size={12} /> Deactivate Failed</button>
          <button onClick={exportHealth} data-testid="export-health-btn"
            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:text-white flex items-center gap-1.5"><Download size={12} /> Export</button>
          <div className="flex items-center gap-1.5 ml-auto">
            <span data-testid="health-mode-badge" className={cn("px-2 py-1 rounded-lg text-[11px] font-semibold",
              healthCfg?.mode === "automatic" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-400")}>
              {healthCfg?.mode === "automatic" ? `Auto · ${healthCfg?.interval}` : "Manual"}
            </span>
            <button onClick={() => { loadHealthCfg(); setShowCfg(true); }} data-testid="configure-monitoring-btn"
              className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:text-white flex items-center gap-1.5">
              <CalendarClock size={13} /> Configure Monitoring
            </button>
          </div>
        </div>

        {showCfg && healthCfg && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="monitoring-config-modal">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 sticky top-0 bg-slate-900">
                <h3 className="text-base font-semibold text-white">Channel Health Monitoring</h3>
                <button onClick={() => setShowCfg(false)} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"><X size={18} /></button>
              </div>
              <div className="px-5 py-4 space-y-4 text-sm">
                <div>
                  <label className="text-xs text-slate-500 block mb-1.5">Mode</label>
                  <div className="flex gap-2">
                    {[["manual", "Manual"], ["automatic", "Automatic"]].map(([v, l]) => (
                      <button key={v} onClick={() => setCfg("mode", v)} data-testid={`cfg-mode-${v}`}
                        className={cn("px-3 py-1.5 rounded-lg text-xs font-medium border", healthCfg.mode === v ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400")}>{l}</button>
                    ))}
                  </div>
                </div>
                {healthCfg.mode === "automatic" && (
                  <>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Frequency</label>
                      <select value={healthCfg.interval} onChange={e => setCfg("interval", e.target.value)} data-testid="cfg-interval"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                        {[["hourly","Every Hour"],["3h","Every 3 Hours"],["6h","Every 6 Hours"],["12h","Every 12 Hours"],["daily","Daily"],["2d","Every 2 Days"],["weekly","Weekly"],["biweekly","Every 2 Weeks"],["monthly","Monthly"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Scope ({healthCfg.channels_included} channels included)</label>
                      <select value={healthCfg.scope} onChange={e => setCfg("scope", e.target.value)} data-testid="cfg-scope"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                        {[["active","All active channels"],["all","All channels"],["premium","Premium channels"],["featured","Featured channels"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Public action policy</label>
                      <select value={healthCfg.action_policy} onChange={e => setCfg("action_policy", e.target.value)} data-testid="cfg-policy"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                        {[["monitor","Monitor Only"],["unavailable","Display Unavailable Message"],["hide","Automatically Hide Channel"],["disable","Automatically Disable Channel"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      {["hide","disable"].includes(healthCfg.action_policy) && (
                        <p className="text-[11px] text-amber-400 mt-1.5 flex items-center gap-1"><AlertTriangle size={11} /> This will automatically {healthCfg.action_policy} failing channels (never deletes).</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Failure threshold</label>
                        <select value={healthCfg.fail_threshold} onChange={e => setCfg("fail_threshold", parseInt(e.target.value))} data-testid="cfg-fail-threshold"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                          {[1,2,3,5].map(n => <option key={n} value={n}>{n} consecutive</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Recovery</label>
                        <select value={healthCfg.recovery} onChange={e => setCfg("recovery", e.target.value)} data-testid="cfg-recovery"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-200">
                          <option value="auto">Auto-restore when healthy</option>
                          <option value="review">Require admin review</option>
                        </select>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" checked={!!healthCfg.use_backup_auto} onChange={e => setCfg("use_backup_auto", e.target.checked)} data-testid="cfg-use-backup" className="rounded border-slate-600" />
                      Automatically use backup stream during an outage (report-only otherwise)
                    </label>
                    {healthCfg.next_scan && <p className="text-[11px] text-slate-500">Next scan: {new Date(healthCfg.next_scan).toLocaleString()}</p>}
                  </>
                )}
                <div className="flex gap-2 pt-2">
                  <button onClick={() => setShowCfg(false)} className="flex-1 px-4 py-2 rounded-lg bg-slate-800 text-slate-300 text-sm">Cancel</button>
                  <button onClick={saveHealthCfg} disabled={cfgSaving} data-testid="cfg-save" className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold text-sm disabled:opacity-50">{cfgSaving ? "Saving…" : "Save"}</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {(selected.size > 0 || allMatching) && (
          <div className="flex flex-col gap-2 px-4 py-2 bg-blue-600/10 border border-blue-500/20 rounded-xl" data-testid="bulk-actions">
            {/* Select-all-matching banner */}
            {!allMatching && selected.size === channels.length && channels.length > 0 && total > channels.length && (
              <div className="text-xs text-slate-300" data-testid="select-all-matching-banner">
                {selected.size} channels on this page are selected.{" "}
                <button onClick={selectAllMatching} className="text-blue-400 font-semibold hover:underline" data-testid="select-all-matching-btn">
                  Select all {total} channels matching these filters
                </button>
              </div>
            )}
            {allMatching && (
              <div className="text-xs text-emerald-400 font-semibold" data-testid="all-matching-active">
                All {effectiveCount} channels matching these filters are selected{excluded.size ? ` (${excluded.size} excluded)` : ""}.
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-blue-400 font-medium" data-testid="bulk-selected-count">{effectiveCount} selected</span>
              <div className="flex gap-1.5 ml-auto flex-wrap">
                {[
                  { action: "activate", label: "Activate", icon: Eye },
                  { action: "deactivate", label: "Deactivate", icon: EyeOff },
                  { action: "feature", label: "Feature", icon: Star },
                  { action: "unfeature", label: "Unfeature", icon: StarOff },
                  { action: "make_premium", label: "Premium", icon: Star },
                  { action: "make_free", label: "Free", icon: StarOff },
                  { action: "publish", label: "Publish", icon: Eye },
                  { action: "make_draft", label: "To Draft", icon: EyeOff },
                  { action: "delete", label: "Delete", icon: Trash2 },
                ].map(({ action, label, icon: Icon }) => (
                  <button key={action} onClick={() => bulkAction(action)} data-testid={`bulk-${action}`}
                    className="px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300 hover:text-white flex items-center gap-1">
                    <Icon size={11} /> {label}
                  </button>
                ))}
                <select onChange={(e) => { if (e.target.value) { bulkAction("assign_category", e.target.value); e.target.value = ""; } }}
                  data-testid="bulk-assign-category" defaultValue=""
                  className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300">
                  <option value="">Assign category…</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select onChange={(e) => { if (e.target.value) { bulkAction("assign_country", e.target.value); e.target.value = ""; } }}
                  data-testid="bulk-assign-country" defaultValue=""
                  className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300">
                  <option value="">Assign country…</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select onChange={(e) => { if (e.target.value) { bulkAction("assign_language", e.target.value); e.target.value = ""; } }}
                  data-testid="bulk-assign-language" defaultValue=""
                  className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-300">
                  <option value="">Assign language…</option>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.name_en}</option>)}
                </select>
                <button onClick={clearSelection} data-testid="bulk-clear" className="px-2.5 py-1 text-xs text-slate-500 hover:text-white">Clear</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-slate-900/50 border border-slate-700/30 rounded-2xl overflow-hidden" data-testid="channels-table">
        <div className="hidden lg:grid grid-cols-[36px_1fr_120px_100px_190px_70px_60px_100px] gap-2 px-4 py-3 border-b border-slate-800 text-[11px] text-slate-500 uppercase tracking-wider font-medium">
          <div><input type="checkbox" checked={allMatching || (channels.length > 0 && selected.size === channels.length)} onChange={toggleSelectAll} className="rounded border-slate-600" data-testid="select-page-checkbox" /></div>
          <button onClick={() => toggleSort("name")} className="flex items-center gap-1 hover:text-white">Channel <ArrowUpDown size={10} /></button>
          <span>Category</span>
          <span>Country</span>
          <span>Health / Last Tested</span>
          <span>Status</span>
          <span>Featured</span>
          <span>Actions</span>
        </div>

        {loading && (
          <div className="py-16 text-center" data-testid="channels-loading">
            <Loader2 size={24} className="text-blue-400 animate-spin mx-auto mb-3" />
            <p className="text-sm text-slate-500">Loading channels...</p>
          </div>
        )}

        {!loading && channels.length === 0 && (
          <div className="py-16 text-center" data-testid="channels-empty">
            <Tv size={32} className="text-slate-600 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-white mb-1">No channels found</h3>
            <p className="text-xs text-slate-500">{search || statusFilter ? "Try adjusting your filters" : "Create your first channel"}</p>
          </div>
        )}

        {!loading && channels.map(ch => (
          <div key={ch.id} className={cn(
            "grid grid-cols-1 lg:grid-cols-[36px_1fr_120px_100px_190px_70px_60px_100px] gap-2 px-4 py-3 border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors items-center",
            ch.deleted_at && "opacity-50"
          )} data-testid={`channel-row-${ch.id}`}>
            <div className="hidden lg:block"><input type="checkbox" checked={isRowSelected(ch.id)} onChange={() => toggleSelect(ch.id)} className="rounded border-slate-600" /></div>
            <div className="flex items-center gap-3 min-w-0">
              <img src={ch.logo_url || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Crect fill='%231e293b' width='36' height='36' rx='8'/%3E%3Ctext x='50%25' y='55%25' text-anchor='middle' dy='.3em' fill='%2364748b' font-size='14'%3ETV%3C/text%3E%3C/svg%3E"} alt="" className="w-9 h-9 rounded-lg object-contain bg-slate-800 border border-slate-700/50 flex-shrink-0"
                onError={e => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Crect fill='%231e293b' width='36' height='36' rx='8'/%3E%3Ctext x='50%25' y='55%25' text-anchor='middle' dy='.3em' fill='%2364748b' font-size='14'%3ETV%3C/text%3E%3C/svg%3E"; }} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{ch.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {ch.is_live ? <span className="flex items-center gap-0.5 text-[9px] text-red-400"><Radio size={8} /> LIVE</span> : null}
                  {ch.is_hd ? <span className="text-[9px] text-cyan-400 font-bold">HD</span> : null}
                  {ch.is_premium ? <span className="flex items-center gap-0.5 text-[9px] text-purple-400"><Crown size={8} /> PRO</span> : null}
                </div>
              </div>
            </div>
            <span className="text-xs text-slate-400 truncate hidden lg:block">{ch.category_name || "—"}</span>
            <div className="items-center gap-1.5 hidden lg:flex">
              {ch.country_flag && <img src={ch.country_flag} alt="" className="w-4 h-3 rounded-sm object-cover" />}
              <span className="text-xs text-slate-400 truncate">{ch.country_name || "—"}</span>
            </div>
            <div className="hidden lg:block"><HealthCell ch={ch} onTest={testRow} testing={testingRow === ch.id} /></div>
            <span className={cn("text-[10px] font-medium px-2 py-0.5 rounded-full w-fit hidden lg:inline-block",
              ch.deleted_at ? "bg-red-500/10 text-red-400" : ch.is_active ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400")}>
              {ch.deleted_at ? "Deleted" : ch.is_active ? "Active" : "Off"}
            </span>
            <span className={cn("text-[10px] font-medium hidden lg:block", ch.is_featured ? "text-amber-400" : "text-slate-600")}>
              {ch.is_featured ? "★ Yes" : "—"}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setEditChannel(ch)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" title="Edit" data-testid={`edit-${ch.id}`}>
                <Settings size={13} />
              </button>
              {!ch.deleted_at && (
                <>
                  <button onClick={() => quickAction(ch.id, "activate", { is_active: ch.is_active ? 0 : 1 })} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" title={ch.is_active ? "Deactivate" : "Activate"}>
                    {ch.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button onClick={() => duplicateChannel(ch.id)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white" title="Duplicate">
                    <Copy size={13} />
                  </button>
                  <button onClick={() => deleteChannel(ch)} className="p-1.5 rounded-lg hover:bg-red-900/30 text-slate-400 hover:text-red-400" title="Delete" data-testid={`delete-${ch.id}`}>
                    <Trash2 size={13} />
                  </button>
                </>
              )}
              {ch.deleted_at && (
                <button onClick={() => restoreChannel(ch.id)} className="p-1.5 rounded-lg hover:bg-green-900/30 text-slate-400 hover:text-green-400" title="Restore">
                  <RotateCcw size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="channels-pagination">
          <span className="text-xs text-slate-500">Page {page} of {pages} ({total} total)</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30" data-testid="prev-page">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              let p;
              if (pages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= pages - 2) p = pages - 4 + i;
              else p = page - 2 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={cn("w-8 h-8 rounded-lg text-xs font-medium",
                    page === p ? "bg-blue-600 text-white" : "bg-slate-800 border border-slate-700 text-slate-400 hover:text-white")}
                  data-testid={`page-${p}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
              className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white disabled:opacity-30" data-testid="next-page">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {editChannel !== null && (
        <Modal title={editChannel.id ? `Edit: ${editChannel.name}` : "New Channel"} onClose={() => setEditChannel(null)} wide>
          <ChannelForm channel={editChannel} categories={categories} countries={countries} onSave={handleSave} onCancel={() => setEditChannel(null)} saving={saving} />
        </Modal>
      )}

      {showProgress && (
        <HealthProgressModal job={job} onCancel={stopBulk} onClose={() => { setShowProgress(false); setJob(null); fetchChannels(); }} />
      )}

      {showImport && (
        <ImportChannels onClose={() => { setShowImport(false); fetchChannels(); }} onDone={fetchChannels} />
      )}
    </>
  );
}
